import React, { useState } from 'react';
import { IconButton, Stack, TextField, TextFieldProps, Typography } from '@mui/material';
import { Address, getFormattedAddressParts } from 'common/modules/atoms/addresses';
import { RiPencilLine } from 'react-icons/ri';
import { useTranslation } from 'services/localization';
import AutoCompleteCountryPicker from 'common/components/pickers/AutoCompleteCountryPicker';

export interface Props {
	value: Address;
	onChange: (address: Address) => void;
}
const AddressField = (props: Props) => {
	const { value, onChange } = props;
	const { t } = useTranslation();

	const [isEditing, setEditing] = useState<boolean>(false);

	const handleAddressPartChange = (field: keyof Address) => (
		e: React.ChangeEvent<HTMLTextAreaElement>,
	) => {
		onChange({
			...value,
			[field]: e.target.value,
		});
	};

	const getAddressFieldProps = (field: keyof Address): Partial<TextFieldProps> => {
		return {
			value: value[field],
			onChange: handleAddressPartChange(field),
		};
	};

	if (!value) return null;

	if (!isEditing) {
		return (
			<Stack direction="row" justifyContent="space-between" alignItems="center">
				<Stack direction="column">
					{getFormattedAddressParts(value).map((part) => (
						<Typography key={part}>{part}</Typography>
					))}
					<Typography variant="body2" color="secondary" sx={{ fontStyle: 'italic' }}>
						{value.details}
					</Typography>
				</Stack>
				<IconButton onClick={() => setEditing(true)}>
					<RiPencilLine />
				</IconButton>
			</Stack>
		);
	}

	return (
		<Stack direction="column" spacing={2}>
			<AddressTextField
				label={t('common:address.address', 'Address')}
				{...getAddressFieldProps('address')}
			/>
			<Stack direction="row" spacing={2}>
				<AddressTextField
					label={t('common:address.zipPostalCode', 'ZIP / Postal code')}
					{...getAddressFieldProps('zipCode')}
				/>
				<AddressTextField
					label={t('common:address.city', 'City')}
					{...getAddressFieldProps('city')}
				/>
			</Stack>
			<Stack direction="row" spacing={2}>
				<AddressTextField
					label={t('common:address.stateProvince', 'State / Province')}
					{...getAddressFieldProps('state')}
				/>
				<AutoCompleteCountryPicker
					label={t('common:address.country', 'Country')}
					value={value.country}
					onChange={(country) => onChange({ ...value, country })}
					fullWidth
					variant="outlined"
					size="small"
				/>
			</Stack>
			<AddressTextField
				label={t('common:address.additionalInformation', 'Additional information')}
				multiline
				rows={4}
				size="medium"
				{...getAddressFieldProps('details')}
			/>
		</Stack>
	);
};

const AddressTextField = (props: TextFieldProps) => <TextField fullWidth size="small" {...props} />;

export default AddressField;
